import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Search, Filter, Loader2, ChevronRight, ChevronLeft,
  BookOpen, Zap, Calendar, Play, FileText, MoreHorizontal,
  Upload, Download, Eye, Copy, Archive, Trash2, CheckCircle2,
  Clock, Users, DollarSign, BarChart3, Settings2, Globe,
  Lock, Star, ChevronDown, ChevronUp, X, ArrowUpDown,
  LayoutGrid, LayoutList, Package, Shield, UserPlus, Share2,
  GraduationCap, AlertTriangle, Edit, ExternalLink, Check
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Progress } from "../../components/ui/progress";
import { Checkbox } from "../../components/ui/checkbox";
import ConfirmDeleteDialog from "../../components/shared/ConfirmDeleteDialog";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { cn } from "../../lib/utils";
import AssignmentModal from "../../components/shared/AssignmentModal";
import BookSessionDialog from "../../components/shared/BookSessionDialog";

const courseTypeConfig = {
  "scorm_1.2": { label: "eLearning", icon: Zap, color: "bg-blue-50 text-blue-700 border-blue-200" },
  "scorm_2004": { label: "eLearning 2004", icon: Zap, color: "bg-blue-50 text-blue-700 border-blue-200" },
  "ilt": { label: "Instructor-Led", icon: Calendar, color: "bg-amber-50 text-amber-700 border-amber-200" },
  "video": { label: "Video", icon: Play, color: "bg-purple-50 text-purple-700 border-purple-200" },
  "document": { label: "Document", icon: FileText, color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const accessModelConfig = {
  subscription: { label: "Subscription", icon: Package, color: "bg-green-50 text-green-700 border-green-200" },
  purchasable: { label: "Purchasable", icon: DollarSign, color: "bg-amber-50 text-amber-700 border-amber-200" },
  free: { label: "Free", icon: Globe, color: "bg-blue-50 text-blue-700 border-blue-200" },
  restricted: { label: "Restricted", icon: Lock, color: "bg-red-50 text-red-700 border-red-200" },
};

const difficultyConfig = {
  beginner: { label: "Beginner", color: "bg-green-50 text-green-700" },
  intermediate: { label: "Intermediate", color: "bg-blue-50 text-blue-700" },
  advanced: { label: "Advanced", color: "bg-amber-50 text-amber-700" },
  expert: { label: "Expert", color: "bg-red-50 text-red-700" },
};

const CATEGORIES = ["Safety", "Technical Skills", "Compliance", "Leadership", "Operations", "Quality", "Environmental", "HR & Onboarding", "Maintenance", "General"];

const CoursesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const base = user?.role === "super_admin" ? "/admin" : "/tenant";
  const fileInputRef = useRef(null);
  const scormCourseRef = useRef(null);

  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterAccess, setFilterAccess] = useState("all");
  const [sortBy, setSortBy] = useState("updated_at");
  const [sortDir, setSortDir] = useState("desc");
  const [viewMode, setViewMode] = useState("table");
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // {type: 'single'|'bulk', id?: string, count?: number}
  const [bulkLoading, setBulkLoading] = useState(false);

  // Wizard state
  const [showWizard, setShowWizard] = useState(false);
  const [wizStep, setWizStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [editCourse, setEditCourse] = useState(null);
  const [wf, setWf] = useState(getDefaultWf());

  // Course detail sheet
  const [detailCourse, setDetailCourse] = useState(null);
  const [courseStats, setCourseStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // SCORM upload
  const [scormUploading, setScormUploading] = useState(false);
  const [scormPreviewUrl, setScormPreviewUrl] = useState(null);
  const [showScormPreview, setShowScormPreview] = useState(false);

  // Assignment
  const [showAssign, setShowAssign] = useState(false);
  const [assignCourse, setAssignCourse] = useState(null);
  const [bookCourse, setBookCourse] = useState(null);

  function getDefaultWf() {
    return {
      title: "", description: "", course_type: "scorm_1.2",
      category: "Safety", catalog_suite: "Craft Skills eLearning", series: "General",
      duration_hours: 1, passing_score: 70, max_attempts: 0,
      access_model: "subscription", price: 0, is_published: false,
      difficulty_level: "intermediate", self_enrollment_enabled: true,
      certificate_enabled: false, expiry_months: 0, renewal_required: false,
      completion_criteria: "progress", instructor_name: "", tags: "",
      code: "",
    };
  }

  useEffect(() => { fetchData(); }, []); // eslint-disable-line

  const fetchData = async () => {
    try {
      const [cr, catR] = await Promise.all([
        api.get("/courses"),
        api.get("/categories").catch(() => ({ data: [] })),
      ]);
      setCourses(cr.data || []);
      setCategories(catR.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    let result = courses.filter(c => {
      const s = !searchQuery || c.title.toLowerCase().includes(searchQuery.toLowerCase()) || (c.description || "").toLowerCase().includes(searchQuery.toLowerCase()) || (c.code || "").toLowerCase().includes(searchQuery.toLowerCase());
      const cat = filterCategory === "all" || c.category === filterCategory;
      const typ = filterType === "all" || c.course_type === filterType || (filterType === "scorm" && c.course_type?.includes("scorm"));
      const pub = filterStatus === "all" || (filterStatus === "published" && c.is_published && !c.is_archived) || (filterStatus === "draft" && !c.is_published && !c.is_archived) || (filterStatus === "archived" && c.is_archived);
      const acc = filterAccess === "all" || (c.access_model || "subscription") === filterAccess;
      return s && cat && typ && pub && acc;
    });
    result.sort((a, b) => {
      let va = a[sortBy], vb = b[sortBy];
      if (sortBy === "title") { va = (va || "").toLowerCase(); vb = (vb || "").toLowerCase(); }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return result;
  }, [courses, searchQuery, filterCategory, filterType, filterStatus, filterAccess, sortBy, sortDir]);

  // Stats
  const totalCourses = courses.length;
  const published = courses.filter(c => c.is_published && !c.is_archived).length;
  const drafts = courses.filter(c => !c.is_published && !c.is_archived).length;
  const archived = courses.filter(c => c.is_archived).length;
  const scormCount = courses.filter(c => c.course_type?.includes("scorm")).length;
  const iltCount = courses.filter(c => c.course_type === "ilt").length;

  const resetWizard = () => { setWizStep(1); setWf(getDefaultWf()); setEditCourse(null); };

  const openEdit = (course) => {
    setEditCourse(course);
    setWf({
      title: course.title || "",
      description: course.description || "",
      course_type: course.course_type || "scorm_1.2",
      category: course.category || "General",
      catalog_suite: course.catalog_suite || "Craft Skills eLearning",
      series: course.series || "General",
      duration_hours: course.duration_hours || 1,
      passing_score: course.passing_score || 70,
      max_attempts: course.max_attempts || 0,
      access_model: course.access_model || "subscription",
      price: course.price || 0,
      is_published: course.is_published || false,
      difficulty_level: course.difficulty_level || "intermediate",
      self_enrollment_enabled: course.self_enrollment_enabled !== false,
      certificate_enabled: course.certificate_enabled || false,
      expiry_months: course.expiry_months || 0,
      renewal_required: course.renewal_required || false,
      completion_criteria: course.completion_criteria || "progress",
      instructor_name: course.instructor_name || "",
      tags: (course.tags || []).join(", "),
      code: course.code || "",
    });
    setWizStep(1);
    setShowWizard(true);
  };

  const handleSave = async () => {
    if (!wf.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...wf,
        duration_hours: parseFloat(wf.duration_hours) || 1,
        passing_score: parseInt(wf.passing_score) || 0,
        max_attempts: parseInt(wf.max_attempts) || 0,
        price: parseFloat(wf.price) || 0,
        expiry_months: parseInt(wf.expiry_months) || 0,
        tags: wf.tags ? wf.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
      };
      if (editCourse) {
        await api.put(`/courses/${editCourse.id}`, payload);
        toast.success("Course updated!");
      } else {
        payload.source = user?.role === "super_admin" ? "tts" : "tenant";
        await api.post("/courses", payload);
        toast.success("Course created!");
      }
      setShowWizard(false);
      resetWizard();
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  // Actions
  const handlePublish = async (id, publish) => {
    try {
      await api.put(`/courses/${id}/publish`, { publish });
      toast.success(publish ? "Published" : "Unpublished");
      fetchData();
    } catch (e) { toast.error("Failed"); }
  };

  const handleArchive = async (id) => {
    try {
      await api.put(`/courses/${id}/archive`);
      toast.success("Archived");
      fetchData();
    } catch (e) { toast.error("Failed"); }
  };

  const handleDuplicate = async (id) => {
    try {
      await api.post(`/courses/${id}/duplicate`);
      toast.success("Course duplicated");
      fetchData();
    } catch (e) { toast.error("Failed"); }
  };

  const handleDelete = async (id) => {
    setDeleteConfirm({ type: "single", id });
  };

  const executeDelete = async () => {
    if (!deleteConfirm) return;
    if (deleteConfirm.type === "single") {
      try {
        await api.delete(`/courses/${deleteConfirm.id}`);
        toast.success("Deleted");
        fetchData();
      } catch (e) { toast.error("Failed"); }
    } else if (deleteConfirm.type === "bulk") {
      setBulkLoading(true);
      try {
        await api.post("/courses/bulk-action", { action: "delete", course_ids: selectedIds });
        toast.success(`Deleted ${selectedIds.length} courses`);
        setSelectedIds([]);
        fetchData();
      } catch (e) { toast.error("Bulk delete failed"); }
      finally { setBulkLoading(false); }
    }
    setDeleteConfirm(null);
  };

  const handleBulkAction = async (action) => {
    if (selectedIds.length === 0) return;
    if (action === "delete") {
      setDeleteConfirm({ type: "bulk", count: selectedIds.length });
      return;
    }
    setBulkLoading(true);
    try {
      await api.post("/courses/bulk-action", { action, course_ids: selectedIds });
      toast.success(`${action} applied to ${selectedIds.length} courses`);
      setSelectedIds([]);
      fetchData();
    } catch (e) { toast.error("Bulk action failed"); }
    finally { setBulkLoading(false); }
  };

  // SCORM Upload
  const handleScormUpload = async (courseId, file) => {
    if (!file || !file.name.endsWith(".zip")) { toast.error("Please select a .zip file"); return; }
    setScormUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post(`/courses/${courseId}/upload-scorm`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`SCORM uploaded: ${res.data.scorm_version || "detected"}`);
      fetchData();
      if (detailCourse) loadCourseDetail(courseId);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setScormUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const loadCourseDetail = async (courseId) => {
    setStatsLoading(true);
    try {
      const [cRes, sRes] = await Promise.all([
        api.get(`/courses/${courseId}`),
        api.get(`/courses/${courseId}/stats`).catch(() => ({ data: null })),
      ]);
      setDetailCourse(cRes.data);
      setCourseStats(sRes.data);
    } catch (e) { console.error(e); }
    finally { setStatsLoading(false); }
  };

  const handlePreviewScorm = async (courseId) => {
    try {
      const res = await api.get(`/courses/${courseId}/scorm-preview`);
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.preview_url}`;
      setScormPreviewUrl(fullUrl);
      setShowScormPreview(true);
    } catch (e) { toast.error(e.response?.data?.detail || "No SCORM content"); }
  };

  const toggleAll = () => {
    if (selectedIds.length === filtered.length) setSelectedIds([]);
    else setSelectedIds(filtered.map(c => c.id));
  };

  const toggleOne = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(field); setSortDir("asc"); }
  };

  const variant = user?.role === "super_admin" ? "super_admin" : "tenant_admin";

  return (
    <AdminLayout title="Course Management" variant={variant}>
      <div className="space-y-5" data-testid="admin-courses-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold" data-testid="courses-heading">Course Management</h2>
            <p className="text-sm text-muted-foreground">Create, manage and distribute learning content</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setViewMode(viewMode === "table" ? "grid" : "table")} className="gap-1.5 text-xs h-8" data-testid="toggle-view-btn">
              {viewMode === "table" ? <LayoutGrid className="w-3.5 h-3.5" /> : <LayoutList className="w-3.5 h-3.5" />}
              {viewMode === "table" ? "Grid" : "Table"}
            </Button>
            <Button onClick={() => { resetWizard(); setShowWizard(true); }} className="gap-1.5 h-8 text-xs" data-testid="create-course-btn">
              <Plus className="w-3.5 h-3.5" /> Create Course
            </Button>
          </div>
        </div>

        {/* Stats Strip */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {[
            { label: "Total", value: totalCourses, icon: BookOpen, color: "text-primary" },
            { label: "Published", value: published, icon: CheckCircle2, color: "text-green-600" },
            { label: "Drafts", value: drafts, icon: Edit, color: "text-amber-600" },
            { label: "Archived", value: archived, icon: Archive, color: "text-muted-foreground" },
            { label: "eLearning", value: scormCount, icon: Zap, color: "text-blue-600" },
            { label: "ILT", value: iltCount, icon: Calendar, color: "text-amber-600" },
          ].map((s, i) => (
            <Card key={i} className="border shadow-none">
              <CardContent className="p-3 flex items-center gap-2.5">
                <s.icon className={cn("w-4 h-4 shrink-0", s.color)} />
                <div><p className="text-lg font-bold leading-none">{s.value}</p><p className="text-[10px] text-muted-foreground">{s.label}</p></div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters & Search */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input type="text" placeholder="Search courses, codes..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 h-9 text-sm" data-testid="course-search" />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[130px] h-9 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[130px] h-9 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="scorm">eLearning</SelectItem>
              <SelectItem value="ilt">ILT</SelectItem>
              <SelectItem value="video">Video</SelectItem>
              <SelectItem value="document">Document</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterAccess} onValueChange={setFilterAccess}>
            <SelectTrigger className="w-[140px] h-9 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Access</SelectItem>
              <SelectItem value="subscription">Subscription</SelectItem>
              <SelectItem value="purchasable">Purchasable</SelectItem>
              <SelectItem value="free">Free</SelectItem>
              <SelectItem value="restricted">Restricted</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[140px] h-9 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {[...new Set([...CATEGORIES, ...categories])].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Bulk Actions Bar */}
        <AnimatePresence>
          {selectedIds.length > 0 && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              className="flex items-center gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20" data-testid="bulk-actions-bar">
              <span className="text-sm font-medium">{selectedIds.length} selected</span>
              <Button size="sm" variant="outline" className="text-xs h-7 gap-1" onClick={() => handleBulkAction("publish")} disabled={bulkLoading}>
                <CheckCircle2 className="w-3 h-3" /> Publish
              </Button>
              <Button size="sm" variant="outline" className="text-xs h-7 gap-1" onClick={() => handleBulkAction("unpublish")} disabled={bulkLoading}>
                <Eye className="w-3 h-3" /> Unpublish
              </Button>
              <Button size="sm" variant="outline" className="text-xs h-7 gap-1" onClick={() => handleBulkAction("archive")} disabled={bulkLoading}>
                <Archive className="w-3 h-3" /> Archive
              </Button>
              <Button size="sm" variant="outline" className="text-xs h-7 gap-1 text-red-600 border-red-200" onClick={() => handleBulkAction("delete")} disabled={bulkLoading}>
                <Trash2 className="w-3 h-3" /> Delete
              </Button>
              <Button size="sm" variant="ghost" className="text-xs h-7 ml-auto" onClick={() => setSelectedIds([])}>
                <X className="w-3 h-3" /> Clear
              </Button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="w-12 h-12 mx-auto text-muted-foreground/20 mb-3" />
            <p className="text-sm text-muted-foreground">No courses found</p>
            <Button onClick={() => { resetWizard(); setShowWizard(true); }} className="mt-3 gap-1.5 text-xs" size="sm">
              <Plus className="w-3.5 h-3.5" /> Create your first course
            </Button>
          </div>
        ) : viewMode === "table" ? (
          /* ── TABLE VIEW ── */
          <Card className="border shadow-none overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="courses-table">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="p-3 w-10"><Checkbox checked={selectedIds.length === filtered.length && filtered.length > 0} onCheckedChange={toggleAll} data-testid="select-all-checkbox" /></th>
                    <th className="p-3 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer" onClick={() => toggleSort("title")}>
                      <div className="flex items-center gap-1">Course {sortBy === "title" && (sortDir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}</div>
                    </th>
                    <th className="p-3 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Type</th>
                    <th className="p-3 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Access</th>
                    <th className="p-3 text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Status</th>
                    <th className="p-3 text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer" onClick={() => toggleSort("enrolled_count")}>
                      <div className="flex items-center justify-center gap-1">Enrolled {sortBy === "enrolled_count" && (sortDir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}</div>
                    </th>
                    <th className="p-3 text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Price</th>
                    <th className="p-3 text-right text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(course => {
                    const tc = courseTypeConfig[course.course_type] || courseTypeConfig["scorm_1.2"];
                    const ac = accessModelConfig[course.access_model || "subscription"];
                    const isSelected = selectedIds.includes(course.id);
                    return (
                      <tr key={course.id} className={cn("border-b hover:bg-muted/20 transition-colors", isSelected && "bg-primary/5")} data-testid={`course-row-${course.id}`}>
                        <td className="p-3"><Checkbox checked={isSelected} onCheckedChange={() => toggleOne(course.id)} /></td>
                        <td className="p-3">
                          <div className="flex items-center gap-3 min-w-0">
                            {course.thumbnail_url ? (
                              <img src={course.thumbnail_url} alt="" className="w-9 h-9 rounded-lg object-cover shrink-0 border" />
                            ) : (
                              <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center shrink-0", tc.color)}>
                                <tc.icon className="w-4 h-4" />
                              </div>
                            )}
                            <div className="min-w-0">
                              <button className="text-sm font-medium hover:text-primary text-left truncate block max-w-[250px]" onClick={() => navigate(`${base}/courses/${course.id}`)}>{course.title}</button>
                              <div className="flex items-center gap-2 mt-0.5">
                                {course.code && <span className="text-[10px] font-mono text-muted-foreground">{course.code}</span>}
                                <span className="text-[10px] text-muted-foreground">{course.category}</span>
                                <span className="text-[10px] text-muted-foreground">{course.duration_hours}h</span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="p-3"><Badge className={cn("text-[9px] px-1.5 py-0 border gap-1", tc.color)}><tc.icon className="w-2.5 h-2.5" />{tc.label}</Badge></td>
                        <td className="p-3">{ac && <Badge className={cn("text-[9px] px-1.5 py-0 border gap-1", ac.color)}><ac.icon className="w-2.5 h-2.5" />{ac.label}</Badge>}</td>
                        <td className="p-3 text-center">
                          {course.is_archived ? (
                            <Badge className="text-[9px] px-1.5 py-0 bg-muted text-muted-foreground border">Archived</Badge>
                          ) : course.is_published ? (
                            <Badge className="text-[9px] px-1.5 py-0 bg-green-50 text-green-700 border-green-200 gap-1"><CheckCircle2 className="w-2.5 h-2.5" />Live</Badge>
                          ) : (
                            <Badge className="text-[9px] px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200 gap-1"><Edit className="w-2.5 h-2.5" />Draft</Badge>
                          )}
                        </td>
                        <td className="p-3 text-center"><span className="text-sm font-medium">{course.enrolled_count || 0}</span></td>
                        <td className="p-3 text-center">
                          {(course.access_model || "subscription") === "purchasable" ? (
                            <span className="text-sm font-bold text-green-700">${(course.price || 0).toFixed(2)}</span>
                          ) : (course.access_model || "subscription") === "free" ? (
                            <span className="text-xs text-muted-foreground">Free</span>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="p-3 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`course-actions-${course.id}`}><MoreHorizontal className="w-4 h-4" /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuItem onClick={() => openEdit(course)}><Edit className="w-3.5 h-3.5 mr-2" />Edit Course</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate(`${base}/courses/${course.id}`)}><Eye className="w-3.5 h-3.5 mr-2" />View Details</DropdownMenuItem>
                              {course.course_type?.includes("scorm") && course.scorm_entry_point && (
                                <DropdownMenuItem onClick={() => handlePreviewScorm(course.id)}><Play className="w-3.5 h-3.5 mr-2" />Preview SCORM</DropdownMenuItem>
                              )}
                              {course.course_type?.includes("scorm") && (
                                <DropdownMenuItem onClick={() => { scormCourseRef.current = course.id; fileInputRef.current?.click(); }}>
                                  <Upload className="w-3.5 h-3.5 mr-2" />Upload SCORM
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => { setAssignCourse(course); setShowAssign(true); }}><UserPlus className="w-3.5 h-3.5 mr-2" />Assign Users</DropdownMenuItem>
                              {course.course_type === "ilt" && (
                                <DropdownMenuItem onClick={() => setBookCourse(course)}><Calendar className="w-3.5 h-3.5 mr-2" />Book Session</DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => handleDuplicate(course.id)}><Copy className="w-3.5 h-3.5 mr-2" />Duplicate</DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {!course.is_published ? (
                                <DropdownMenuItem onClick={() => handlePublish(course.id, true)}><CheckCircle2 className="w-3.5 h-3.5 mr-2" />Publish</DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={() => handlePublish(course.id, false)}><Eye className="w-3.5 h-3.5 mr-2" />Unpublish</DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => handleArchive(course.id)}><Archive className="w-3.5 h-3.5 mr-2" />Archive</DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem className="text-red-600" onClick={() => handleDelete(course.id)}><Trash2 className="w-3.5 h-3.5 mr-2" />Delete</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="p-3 border-t text-xs text-muted-foreground">Showing {filtered.length} of {totalCourses} courses</div>
          </Card>
        ) : (
          /* ── GRID VIEW ── */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((course, i) => {
              const tc = courseTypeConfig[course.course_type] || courseTypeConfig["scorm_1.2"];
              const ac = accessModelConfig[course.access_model || "subscription"];
              return (
                <motion.div key={course.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.02, 0.5) }}>
                  <Card className="card-hover cursor-pointer h-full border shadow-none group" onClick={() => navigate(`${base}/courses/${course.id}`)} data-testid={`course-card-${course.id}`}>
                    <CardContent className="p-0">
                      <div className="h-28 relative overflow-hidden rounded-t-lg">
                        {course.thumbnail_url ? (
                          <img src={course.thumbnail_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                        ) : (
                          <div className={cn("absolute inset-0", i % 4 === 0 ? "bg-gradient-to-br from-blue-600 to-indigo-700" : i % 4 === 1 ? "bg-gradient-to-br from-emerald-600 to-teal-700" : i % 4 === 2 ? "bg-gradient-to-br from-amber-600 to-orange-700" : "bg-gradient-to-br from-violet-600 to-purple-700")} />
                        )}
                        {!course.thumbnail_url && <div className="absolute inset-0 flex items-center justify-center"><tc.icon className="w-10 h-10 text-white/15" /></div>}
                        <div className="absolute top-2.5 right-2.5 flex items-center gap-1.5">
                          {course.is_published ? <Badge className="bg-green-500/90 text-white border-0 text-[9px] px-1.5">Live</Badge> : <Badge className="bg-black/40 text-white border-0 text-[9px] px-1.5">Draft</Badge>}
                        </div>
                        <div className="absolute bottom-2.5 left-2.5"><Badge className="bg-white/20 text-white border-0 text-[9px]">{tc.label}</Badge></div>
                      </div>
                      <div className="p-3.5 space-y-2.5">
                        <div>
                          <h3 className="font-semibold line-clamp-1 text-sm">{course.title}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            {course.code && <span className="text-[10px] font-mono text-muted-foreground">{course.code}</span>}
                            <span className="text-[10px] text-muted-foreground">{course.category}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={cn("text-[9px] px-1.5 py-0 border", ac?.color)}>{ac?.label}</Badge>
                          {(course.access_model || "subscription") === "purchasable" && (
                            <span className="text-xs font-bold text-green-700">${(course.price || 0).toFixed(2)}</span>
                          )}
                          <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{course.duration_hours}h</span>
                          {(course.enrolled_count || 0) > 0 && <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><Users className="w-2.5 h-2.5" />{course.enrolled_count}</span>}
                        </div>
                        <div className="flex items-center justify-between pt-2 border-t">
                          <Badge className={cn("text-[9px]", difficultyConfig[course.difficulty_level || "intermediate"]?.color)}>{difficultyConfig[course.difficulty_level || "intermediate"]?.label}</Badge>
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={e => { e.stopPropagation(); openEdit(course); }}><Edit className="w-3 h-3" /></Button>
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={e => { e.stopPropagation(); handleDuplicate(course.id); }}><Copy className="w-3 h-3" /></Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Hidden SCORM file input */}
      <input ref={fileInputRef} type="file" accept=".zip" className="hidden" onChange={e => { if (scormCourseRef.current && e.target.files?.[0]) handleScormUpload(scormCourseRef.current, e.target.files[0]); }} />

      {/* ── CREATE/EDIT WIZARD ── */}
      <Dialog open={showWizard} onOpenChange={v => { if (!v) { setShowWizard(false); resetWizard(); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base">{editCourse ? "Edit Course" : "Create New Course"}</DialogTitle>
          </DialogHeader>

          {/* Step indicator */}
          <div className="flex items-center gap-1 mb-1">
            {["Basics", "Type & Access", "Settings", "Completion", "Review"].map((label, i) => {
              const s = i + 1;
              return (
                <div key={s} className="flex items-center flex-1">
                  <button onClick={() => setWizStep(s)} className={cn("w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors", s < wizStep ? "bg-green-500 text-white" : s === wizStep ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>
                    {s < wizStep ? <Check className="w-3.5 h-3.5" /> : s}
                  </button>
                  {i < 4 && <div className={cn("flex-1 h-0.5 mx-1", s < wizStep ? "bg-green-500" : "bg-muted")} />}
                </div>
              );
            })}
          </div>
          <div className="flex justify-between text-[10px] text-muted-foreground mb-4">
            {["Basics", "Type & Access", "Settings", "Completion", "Review"].map(l => <span key={l}>{l}</span>)}
          </div>

          {/* Step 1: Basics */}
          {wizStep === 1 && (
            <div className="space-y-4" data-testid="wizard-step-1">
              <div><Label className="text-xs">Course Title *</Label><Input value={wf.title} onChange={e => setWf({...wf, title: e.target.value})} placeholder="e.g., Workplace Safety Fundamentals" className="text-sm h-9" data-testid="wiz-title" /></div>
              <div><Label className="text-xs">Description</Label><Textarea value={wf.description} onChange={e => setWf({...wf, description: e.target.value})} placeholder="Brief description..." rows={3} className="text-sm" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs">Course Code</Label><Input value={wf.code} onChange={e => setWf({...wf, code: e.target.value})} placeholder="e.g., SAF-101" className="text-sm h-9" /></div>
                <div><Label className="text-xs">Instructor</Label><Input value={wf.instructor_name} onChange={e => setWf({...wf, instructor_name: e.target.value})} placeholder="Instructor name" className="text-sm h-9" /></div>
              </div>
              <div><Label className="text-xs">Tags (comma separated)</Label><Input value={wf.tags} onChange={e => setWf({...wf, tags: e.target.value})} placeholder="safety, compliance, manufacturing" className="text-sm h-9" /></div>
            </div>
          )}

          {/* Step 2: Type & Access */}
          {wizStep === 2 && (
            <div className="space-y-4" data-testid="wizard-step-2">
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs">Course Type</Label>
                  <Select value={wf.course_type} onValueChange={v => setWf({...wf, course_type: v})}>
                    <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="scorm_1.2">eLearning (SCORM 1.2)</SelectItem>
                      <SelectItem value="scorm_2004">eLearning (SCORM 2004)</SelectItem>
                      <SelectItem value="ilt">Instructor-Led (ILT)</SelectItem>
                      <SelectItem value="video">Video Course</SelectItem>
                      <SelectItem value="document">Document</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Category</Label>
                  <Select value={wf.category} onValueChange={v => setWf({...wf, category: v})}>
                    <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label className="text-xs font-semibold mb-2 block">Access Model</Label>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(accessModelConfig).map(([key, cfg]) => (
                    <button key={key} onClick={() => setWf({...wf, access_model: key})}
                      className={cn("p-3 rounded-lg border-2 text-left transition-all", wf.access_model === key ? "border-primary bg-primary/5" : "border-border hover:border-primary/30")}
                      data-testid={`access-${key}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <cfg.icon className="w-4 h-4" />
                        <span className="text-sm font-medium">{cfg.label}</span>
                      </div>
                      <p className="text-[10px] text-muted-foreground">
                        {key === "subscription" && "Included in tenant subscription"}
                        {key === "purchasable" && "One-time purchase per learner"}
                        {key === "free" && "Available to all at no cost"}
                        {key === "restricted" && "Admin assigns to specific tenants"}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
              {wf.access_model === "purchasable" && (
                <div className="p-4 rounded-lg border-2 border-amber-200 bg-amber-50/30">
                  <Label className="text-xs font-semibold">Course Price (USD) *</Label>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-lg font-bold">$</span>
                    <Input type="number" min="0" step="0.01" value={wf.price} onChange={e => setWf({...wf, price: e.target.value})} className="text-sm h-9 max-w-[150px]" data-testid="wiz-price" />
                  </div>
                </div>
              )}
              <div><Label className="text-xs">Difficulty Level</Label>
                <Select value={wf.difficulty_level} onValueChange={v => setWf({...wf, difficulty_level: v})}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(difficultyConfig).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Step 3: Settings */}
          {wizStep === 3 && (
            <div className="space-y-4" data-testid="wizard-step-3">
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs">Duration (hours)</Label><Input type="number" min="0.5" step="0.5" value={wf.duration_hours} onChange={e => setWf({...wf, duration_hours: e.target.value})} className="text-sm h-9" /></div>
                <div><Label className="text-xs">Catalog Suite</Label><Input value={wf.catalog_suite} onChange={e => setWf({...wf, catalog_suite: e.target.value})} className="text-sm h-9" /></div>
              </div>
              <div><Label className="text-xs">Series</Label><Input value={wf.series} onChange={e => setWf({...wf, series: e.target.value})} className="text-sm h-9" /></div>
              <div className="space-y-3 pt-2">
                <div className="flex items-center gap-3 p-3 rounded-lg border">
                  <input type="checkbox" checked={wf.self_enrollment_enabled} onChange={e => setWf({...wf, self_enrollment_enabled: e.target.checked})} className="rounded" id="self_enroll" />
                  <label htmlFor="self_enroll" className="text-sm cursor-pointer"><span className="font-medium">Self-enrollment</span><p className="text-[10px] text-muted-foreground">Learners can enroll themselves</p></label>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg border">
                  <input type="checkbox" checked={wf.certificate_enabled} onChange={e => setWf({...wf, certificate_enabled: e.target.checked})} className="rounded" id="cert" />
                  <label htmlFor="cert" className="text-sm cursor-pointer"><span className="font-medium">Certificate on completion</span><p className="text-[10px] text-muted-foreground">Issue certificate when course is completed</p></label>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg border">
                  <input type="checkbox" checked={wf.is_published} onChange={e => setWf({...wf, is_published: e.target.checked})} className="rounded" id="pub" />
                  <label htmlFor="pub" className="text-sm cursor-pointer"><span className="font-medium">Publish immediately</span><p className="text-[10px] text-muted-foreground">Make available in catalog right away</p></label>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Completion */}
          {wizStep === 4 && (
            <div className="space-y-4" data-testid="wizard-step-4">
              <div><Label className="text-xs">Completion Criteria</Label>
                <Select value={wf.completion_criteria} onValueChange={v => setWf({...wf, completion_criteria: v})}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="progress">Progress (100% viewed)</SelectItem>
                    <SelectItem value="score">Minimum Score</SelectItem>
                    <SelectItem value="manual">Manual Approval</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {wf.completion_criteria === "score" && (
                <div><Label className="text-xs">Passing Score (%)</Label><Input type="number" min="0" max="100" value={wf.passing_score} onChange={e => setWf({...wf, passing_score: e.target.value})} className="text-sm h-9" /></div>
              )}
              <div><Label className="text-xs">Max Attempts (0 = unlimited)</Label><Input type="number" min="0" value={wf.max_attempts} onChange={e => setWf({...wf, max_attempts: e.target.value})} className="text-sm h-9" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs">Expiry (months, 0 = never)</Label><Input type="number" min="0" value={wf.expiry_months} onChange={e => setWf({...wf, expiry_months: e.target.value})} className="text-sm h-9" /></div>
                <div className="flex items-center gap-3 p-3 rounded-lg border self-end">
                  <input type="checkbox" checked={wf.renewal_required} onChange={e => setWf({...wf, renewal_required: e.target.checked})} className="rounded" id="renew" />
                  <label htmlFor="renew" className="text-xs cursor-pointer font-medium">Require Renewal</label>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Review */}
          {wizStep === 5 && (
            <div className="space-y-3" data-testid="wizard-step-5">
              <div className="p-4 rounded-xl border space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold text-base">{wf.title || "(No title)"}</h4>
                    {wf.code && <span className="text-xs font-mono text-muted-foreground">{wf.code}</span>}
                  </div>
                  {wf.is_published ? <Badge className="bg-green-50 text-green-700 border-green-200 text-[10px]">Published</Badge> : <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">Draft</Badge>}
                </div>
                {wf.description && <p className="text-xs text-muted-foreground">{wf.description}</p>}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-3 border-t text-xs">
                  <div><span className="text-muted-foreground block text-[10px]">Type</span>{courseTypeConfig[wf.course_type]?.label}</div>
                  <div><span className="text-muted-foreground block text-[10px]">Category</span>{wf.category}</div>
                  <div><span className="text-muted-foreground block text-[10px]">Duration</span>{wf.duration_hours}h</div>
                  <div><span className="text-muted-foreground block text-[10px]">Access</span>{accessModelConfig[wf.access_model]?.label}</div>
                  {wf.access_model === "purchasable" && <div><span className="text-muted-foreground block text-[10px]">Price</span><span className="font-bold text-green-700">${parseFloat(wf.price || 0).toFixed(2)}</span></div>}
                  <div><span className="text-muted-foreground block text-[10px]">Difficulty</span>{difficultyConfig[wf.difficulty_level]?.label}</div>
                  <div><span className="text-muted-foreground block text-[10px]">Completion</span>{wf.completion_criteria}</div>
                  {wf.completion_criteria === "score" && <div><span className="text-muted-foreground block text-[10px]">Passing</span>{wf.passing_score}%</div>}
                  <div><span className="text-muted-foreground block text-[10px]">Self-enroll</span>{wf.self_enrollment_enabled ? "Yes" : "No"}</div>
                  <div><span className="text-muted-foreground block text-[10px]">Certificate</span>{wf.certificate_enabled ? "Yes" : "No"}</div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            {wizStep > 1 && <Button variant="outline" size="sm" onClick={() => setWizStep(wizStep - 1)} className="gap-1"><ChevronLeft className="w-3.5 h-3.5" /> Back</Button>}
            <div className="flex-1" />
            {wizStep < 5 ? (
              <Button size="sm" onClick={() => { if (wizStep === 1 && !wf.title.trim()) { toast.error("Title is required"); return; } setWizStep(wizStep + 1); }} className="gap-1" data-testid="wiz-next">
                Next <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            ) : (
              <Button size="sm" onClick={handleSave} disabled={saving} className="gap-1" data-testid="wiz-save">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                {editCourse ? "Save Changes" : "Create Course"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── COURSE DETAIL PANEL ── */}
      <Dialog open={!!detailCourse} onOpenChange={v => { if (!v) { setDetailCourse(null); setCourseStats(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {detailCourse && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle className="text-base">{detailCourse.title}</DialogTitle>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" className="text-xs h-7 gap-1" onClick={() => { setDetailCourse(null); openEdit(detailCourse); }}>
                      <Edit className="w-3 h-3" /> Edit
                    </Button>
                    {detailCourse.course_type?.includes("scorm") && (
                      <Button size="sm" variant="outline" className="text-xs h-7 gap-1" onClick={() => { scormCourseRef.current = detailCourse.id; fileInputRef.current?.click(); }}>
                        {scormUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Upload SCORM
                      </Button>
                    )}
                  </div>
                </div>
              </DialogHeader>

              <Tabs defaultValue="overview" className="mt-2">
                <TabsList className="h-8">
                  <TabsTrigger value="overview" className="text-xs h-7">Overview</TabsTrigger>
                  <TabsTrigger value="analytics" className="text-xs h-7">Analytics</TabsTrigger>
                  {detailCourse.course_type?.includes("scorm") && <TabsTrigger value="scorm" className="text-xs h-7">SCORM</TabsTrigger>}
                  <TabsTrigger value="settings" className="text-xs h-7">Settings</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: "Type", value: courseTypeConfig[detailCourse.course_type]?.label || detailCourse.course_type },
                      { label: "Access", value: accessModelConfig[detailCourse.access_model || "subscription"]?.label },
                      { label: "Status", value: detailCourse.is_archived ? "Archived" : detailCourse.is_published ? "Published" : "Draft" },
                      { label: "Duration", value: `${detailCourse.duration_hours}h` },
                    ].map((item, i) => (
                      <div key={i} className="p-3 rounded-lg border">
                        <p className="text-[10px] text-muted-foreground">{item.label}</p>
                        <p className="text-sm font-medium mt-0.5">{item.value}</p>
                      </div>
                    ))}
                  </div>
                  {detailCourse.description && <p className="text-sm text-muted-foreground">{detailCourse.description}</p>}
                  <div className="flex flex-wrap gap-1.5">
                    {(detailCourse.tags || []).map(t => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}
                  </div>
                  {(detailCourse.access_model || "subscription") === "purchasable" && (
                    <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                      <p className="text-xs text-green-600 font-medium">Purchasable Course</p>
                      <p className="text-2xl font-bold text-green-700 mt-1">${(detailCourse.price || 0).toFixed(2)} <span className="text-xs font-normal">USD</span></p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="analytics" className="space-y-4 mt-4">
                  {statsLoading ? (
                    <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
                  ) : courseStats ? (
                    <>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div className="p-3 rounded-lg border"><p className="text-[10px] text-muted-foreground">Enrolled</p><p className="text-xl font-bold">{courseStats.total_enrollments}</p></div>
                        <div className="p-3 rounded-lg border"><p className="text-[10px] text-muted-foreground">Completed</p><p className="text-xl font-bold text-green-600">{courseStats.completed}</p></div>
                        <div className="p-3 rounded-lg border"><p className="text-[10px] text-muted-foreground">Completion Rate</p><p className="text-xl font-bold">{courseStats.completion_rate}%</p></div>
                        <div className="p-3 rounded-lg border"><p className="text-[10px] text-muted-foreground">Avg Progress</p><p className="text-xl font-bold">{courseStats.avg_progress}%</p></div>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="p-3 rounded-lg border"><p className="text-[10px] text-muted-foreground">Avg Score</p><p className="text-lg font-bold">{courseStats.avg_score}</p></div>
                        <div className="p-3 rounded-lg border"><p className="text-[10px] text-muted-foreground">Total Hours</p><p className="text-lg font-bold">{courseStats.total_time_hours}h</p></div>
                        {courseStats.revenue > 0 && <div className="p-3 rounded-lg bg-green-50 border border-green-200"><p className="text-[10px] text-green-600">Revenue</p><p className="text-lg font-bold text-green-700">${courseStats.revenue.toFixed(2)}</p></div>}
                      </div>
                      <div>
                        <h4 className="text-xs font-semibold mb-2">Enrollment Funnel</h4>
                        <div className="space-y-2">
                          {[
                            { label: "Enrolled", value: courseStats.total_enrollments, color: "bg-blue-500" },
                            { label: "In Progress", value: courseStats.in_progress, color: "bg-amber-500" },
                            { label: "Completed", value: courseStats.completed, color: "bg-green-500" },
                            { label: "Not Started", value: courseStats.not_started, color: "bg-muted" },
                          ].map(item => (
                            <div key={item.label} className="flex items-center gap-3">
                              <span className="text-[10px] text-muted-foreground w-20">{item.label}</span>
                              <div className="flex-1 h-3 rounded-full bg-muted/50 overflow-hidden">
                                <div className={cn("h-full rounded-full transition-all", item.color)} style={{ width: `${courseStats.total_enrollments > 0 ? (item.value / courseStats.total_enrollments) * 100 : 0}%` }} />
                              </div>
                              <span className="text-xs font-medium w-8 text-right">{item.value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : <p className="text-sm text-muted-foreground text-center py-8">No analytics data available</p>}
                </TabsContent>

                {detailCourse.course_type?.includes("scorm") && (
                  <TabsContent value="scorm" className="space-y-4 mt-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold">SCORM Package</h4>
                      {detailCourse.scorm_entry_point && (
                        <Button size="sm" variant="outline" className="text-xs h-7 gap-1" onClick={() => handlePreviewScorm(detailCourse.id)} data-testid="preview-scorm-btn">
                          <Play className="w-3 h-3" /> Preview Course
                        </Button>
                      )}
                    </div>
                    {detailCourse.scorm_entry_point ? (
                      <div className="space-y-3">
                        <div className="p-3 rounded-lg border bg-blue-50/30 flex items-center gap-3">
                          <Package className="w-8 h-8 text-blue-600" />
                          <div>
                            <p className="text-sm font-medium">SCORM Package Installed</p>
                            <p className="text-[10px] text-muted-foreground">Version: {detailCourse.scorm_version || "1.2"} &middot; Entry: {detailCourse.scorm_entry_point}</p>
                          </div>
                        </div>
                        {(detailCourse.scorm_versions || []).length > 0 && (
                          <div>
                            <h5 className="text-xs font-semibold mb-2">Version History</h5>
                            <div className="space-y-1.5">
                              {(detailCourse.scorm_versions || []).map((v, i) => (
                                <div key={i} className="flex items-center justify-between p-2.5 rounded-lg border text-xs">
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline" className="text-[9px]">v{v.version}</Badge>
                                    <span>{v.filename}</span>
                                  </div>
                                  <div className="flex items-center gap-3 text-muted-foreground">
                                    <span>{v.uploaded_by_name}</span>
                                    <span>{new Date(v.uploaded_at).toLocaleDateString()}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8 border-2 border-dashed rounded-xl">
                        <Upload className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
                        <p className="text-sm text-muted-foreground">No SCORM package uploaded</p>
                        <Button size="sm" className="mt-3 gap-1.5 text-xs" onClick={() => { scormCourseRef.current = detailCourse.id; fileInputRef.current?.click(); }}>
                          <Upload className="w-3.5 h-3.5" /> Upload SCORM Package (.zip)
                        </Button>
                      </div>
                    )}
                  </TabsContent>
                )}

                <TabsContent value="settings" className="space-y-3 mt-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {[
                      { label: "Self-enrollment", value: detailCourse.self_enrollment_enabled !== false ? "Enabled" : "Disabled" },
                      { label: "Certificate", value: detailCourse.certificate_enabled ? "Enabled" : "Disabled" },
                      { label: "Completion", value: detailCourse.completion_criteria || "progress" },
                      { label: "Passing Score", value: `${detailCourse.passing_score || 0}%` },
                      { label: "Max Attempts", value: (detailCourse.max_attempts || 0) === 0 ? "Unlimited" : detailCourse.max_attempts },
                      { label: "Expiry", value: (detailCourse.expiry_months || 0) === 0 ? "Never" : `${detailCourse.expiry_months} months` },
                      { label: "Renewal", value: detailCourse.renewal_required ? "Required" : "Not required" },
                      { label: "Difficulty", value: difficultyConfig[detailCourse.difficulty_level || "intermediate"]?.label },
                    ].map((s, i) => (
                      <div key={i} className="flex items-center justify-between p-2.5 rounded-lg border">
                        <span className="text-xs text-muted-foreground">{s.label}</span>
                        <span className="text-xs font-medium">{s.value}</span>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ── SCORM PREVIEW ── */}
      <Dialog open={showScormPreview} onOpenChange={setShowScormPreview}>
        <DialogContent className="max-w-5xl h-[85vh]">
          <DialogHeader>
            <DialogTitle className="text-sm">SCORM Course Preview</DialogTitle>
          </DialogHeader>
          {scormPreviewUrl && (
            <iframe
              src={scormPreviewUrl}
              className="w-full flex-1 rounded-lg border"
              style={{ height: "calc(85vh - 100px)" }}
              title="SCORM Preview"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              data-testid="scorm-preview-iframe"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Assignment Modal */}
      <AssignmentModal
        open={showAssign}
        onOpenChange={setShowAssign}
        title={assignCourse?.title || ""}
        onAssign={async (userIds) => {
          if (!assignCourse) return;
          let success = 0, failed = 0;
          for (const userId of userIds) {
            try { await api.post("/enrollments", { user_id: userId, course_id: assignCourse.id }); success++; } catch { failed++; }
          }
          if (success > 0) toast.success(`Enrolled ${success} users`);
          if (failed > 0) toast.error(`${failed} failed`);
          fetchData();
        }}
      />

      {/* Book Session Dialog */}
      <BookSessionDialog
        open={!!bookCourse}
        onOpenChange={(v) => { if (!v) setBookCourse(null); }}
        courseId={bookCourse?.id || ""}
        courseTitle={bookCourse?.title || ""}
      />
      <ConfirmDeleteDialog
        open={!!deleteConfirm}
        onOpenChange={(v) => { if (!v) setDeleteConfirm(null); }}
        title={deleteConfirm?.type === "bulk" ? `Delete ${deleteConfirm?.count} Courses` : "Delete Course"}
        description={deleteConfirm?.type === "bulk"
          ? `This will permanently delete ${deleteConfirm?.count} courses and all their enrollments, progress data, and modules.`
          : "This will permanently delete this course and all associated enrollments, progress data, and modules."
        }
        confirmWord="DELETE"
        onConfirm={executeDelete}
        confirmLabel={deleteConfirm?.type === "bulk" ? `Delete ${deleteConfirm?.count} Courses` : "Delete Course"}
      />
    </AdminLayout>
  );
};

export default CoursesPage;
